import { useEffect, useState } from 'react'
import { useSaveProfile } from '../../api/hooks'
import { usePush } from '../timer/usePush'
import { Prose } from '../../components/Prose'
import { ageFrom, weightAtBmi } from '../../lib/body'
import { GOAL_LABEL, RATE_OPTIONS, goalOf, type Goal } from '../../lib/energy'
import { todayIso } from '../../lib/format'
import type { Profile } from '../../lib/metrics'

const WEEKDAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

/**
 * Ayarlar tek yerde — ve artık AÇIK bir "Kaydet" düğmesiyle.
 *
 * Önceki hali her alanı odaktan çıkınca sessizce kaydediyordu. Teknik olarak
 * çalışıyordu ama kullanıcı tarafında "kaydettim mi, kaydetmedim mi" sorusunun
 * cevabı yoktu: hiçbir düğme, hiçbir onay, hiçbir iz. Telefonda klavyeyi
 * kapatmadan sekme değiştirince gerçekten de kaydedilmiyordu.
 *
 * Şimdi: yerel taslak + tek kaydet düğmesi + "kaydedildi" geri bildirimi.
 * Kaydedilmemiş değişiklik varken düğme şeridi ekranın altına yapışıyor,
 * böylece uzun formda düğmeyi aramak için sona kadar inmek gerekmiyor.
 */
export function SettingsTab({ profile, isAdmin }: { profile: Profile; isAdmin: boolean }) {
  const saveProfile = useSaveProfile()
  const push = usePush()

  const [draft, setDraft] = useState<Profile>(profile)
  const [saved, setSaved] = useState(false)

  const dirty = JSON.stringify({ ...draft, updatedAt: '' }) !== JSON.stringify({ ...profile, updatedAt: '' })

  // Sunucudan yeni bir profil gelirse (başka cihazdan değişmiş olabilir) ve
  // elimizde kaydedilmemiş bir taslak yoksa senkronize ol.
  useEffect(() => {
    if (!dirty) setDraft(profile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.updatedAt])

  const set = (patch: Partial<Profile>) => {
    setSaved(false)
    setDraft((d) => ({ ...d, ...patch }))
  }

  const goal = goalOf(draft.goal)
  const age = draft.birthDate ? ageFrom(draft.birthDate, todayIso()) : draft.age

  const save = () =>
    saveProfile.mutate(
      { ...draft, updatedAt: new Date().toISOString() },
      { onSuccess: () => setSaved(true) },
    )

  const hataliBoy = draft.heightCm < 100 || draft.heightCm > 250
  const hataliKilo = draft.startWeightKg < 30 || draft.targetWeightKg < 30

  return (
    <>
      <div className="card">
        <h2>Kişisel</h2>
        <Prose className="card-h">
          Bu alanlar bazal metabolizma ve yağ oranı hesabının tamamını belirliyor —
          yanlışsa panelin gösterdiği her sayı yanlış olur.
        </Prose>
        <div className="grid g3">
          <div>
            <label className="f">Boy (cm)</label>
            <input type="number" min={100} max={250} value={draft.heightCm || ''}
                   placeholder="örn. 178"
                   onChange={(e) => set({ heightCm: +e.target.value })} />
          </div>
          <div>
            <label className="f">Doğum tarihin</label>
            <input type="date" value={draft.birthDate ?? ''}
                   onChange={(e) => set({ birthDate: e.target.value || null })} />
            <span className="d" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              {draft.birthDate ? `${age} yaş` : 'yaş bundan hesaplanıyor'}
            </span>
          </div>
          <div>
            <label className="f">Cinsiyet</label>
            <select value={draft.sex === 'female' ? 'female' : 'male'}
                    onChange={(e) => set({ sex: e.target.value as 'male' | 'female' })}>
              <option value="male">Erkek</option>
              <option value="female">Kadın</option>
            </select>
          </div>
        </div>
        {hataliBoy && (
          <p className="note" style={{ color: 'var(--crit)' }}>Boy 100–250 cm aralığında olmalı.</p>
        )}
      </div>

      <div className="card">
        <h2>Hedef</h2>
        <Prose className="card-h">
          Kalori hedefi, hız kontrolü ve uyarı metinleri bu seçime göre değişiyor.
          Kilo vermekle kas almak aynı matematiği kullanmıyor.
        </Prose>

        <div className="splitgrid" style={{ marginBottom: 14 }}>
          {(['lose', 'maintain', 'gain'] as Goal[]).map((g) => (
            <button key={g} className={goal === g ? 'splitcard on' : 'splitcard'}
                    aria-pressed={goal === g}
                    onClick={() => set({ goal: g, weeklyRateKg: RATE_OPTIONS[g][0].value })}>
              <span className="splitcard-name">{GOAL_LABEL[g]}</span>
              <span className="splitcard-days">
                {g === 'lose' ? 'Haftalık açık, güvenli taban korunur'
                  : g === 'maintain' ? 'Harcamayla aynı; ±0.25 kg bandı'
                  : 'Kontrollü fazlalık, en fazla +500 kcal'}
              </span>
            </button>
          ))}
        </div>

        <div className="grid g4">
          <div>
            <label className="f">Başlangıç kilosu (kg)</label>
            <input type="number" step="0.1" value={draft.startWeightKg || ''}
                   placeholder="örn. 82.5"
                   onChange={(e) => set({ startWeightKg: +e.target.value })} />
          </div>
          <div>
            <label className="f">Hedef kilo (kg)</label>
            <input type="number" step="0.1" value={draft.targetWeightKg || ''}
                   placeholder="örn. 75"
                   onChange={(e) => set({ targetWeightKg: +e.target.value })} />
            {draft.heightCm >= 100 && (
              <span className="d" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                VKİ 25 = {weightAtBmi(25, draft.heightCm).toFixed(1)} kg ·
                VKİ 30 = {weightAtBmi(30, draft.heightCm).toFixed(1)} kg
              </span>
            )}
          </div>
          <div>
            <label className="f">
              {goal === 'gain' ? 'Haftalık hedef artış'
                : goal === 'maintain' ? 'Haftalık hedef' : 'Haftalık hedef kayıp'}
            </label>
            <select value={draft.weeklyRateKg}
                    onChange={(e) => set({ weeklyRateKg: +e.target.value })}
                    disabled={goal === 'maintain'}>
              {RATE_OPTIONS[goal].map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Bebek planı sana özel: doğum tarihi girilince faz kartı, hamilelik
              notları ve Bebek Planı sekmesi açılıyor. Diğer kullanıcılarda bu
              alanın görünmesi hem anlamsız hem kafa karıştırıcıydı. */}
          {isAdmin && (
            <div>
              <label className="f">Bebeğin tahmini doğum tarihi</label>
              <input type="date" value={draft.dueDate ?? ''}
                     onChange={(e) => set({ dueDate: e.target.value || null })} />
              <span className="d" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                Boş bırakırsan bebekle ilgili hiçbir bölüm görünmez
              </span>
            </div>
          )}
        </div>

        {hataliKilo && (
          <p className="note" style={{ color: 'var(--crit)' }}>
            Başlangıç ve hedef kilo 30 kg'ın üstünde olmalı.
          </p>
        )}

        <Prose className="note">
          {goal === 'lose'
            ? "Güvenli bant vücut ağırlığının haftada %0.5–0.75'i. Daha hızlısı kaybın daha "
              + 'büyük kısmının kastan gelmesi demek ve metabolik adaptasyonu hızlandırıyor.'
            : goal === 'gain'
            ? 'Haftada %0.25–0.5 vücut ağırlığından hızlısı, fazlalığın büyük kısmının yağa '
              + 'gitmesi demek. Kas kazanımının bir hız sınırı var ve daha çok yemek onu aşmıyor.'
            : 'Koruma bandı ±0.25 kg/hafta. Günlük dalgalanma (tuz, su, bağırsak) zaten 1–2 kg '
              + 'olabiliyor; tek bir tartıya değil çizginin yönüne bakılıyor.'}
        </Prose>
      </div>

      <div className="card">
        <h2>Öğün saatleri</h2>
        <Prose className="card-h">
          Hatırlatıcılar ve günün planı bu saatlerden türetiliyor. Spor olan günlerde
          ikinci öğün otomatik kayıyor — burayı değiştirmene gerek yok.
        </Prose>
        <div className="grid g3">
          <div>
            <label className="f">1. öğün</label>
            <input type="time" value={draft.meal1.slice(0, 5)}
                   onChange={(e) => set({ meal1: e.target.value + ':00' })} />
          </div>
          <div>
            <label className="f">2. öğün</label>
            <input type="time" value={draft.meal2.slice(0, 5)}
                   onChange={(e) => set({ meal2: e.target.value + ':00' })} />
          </div>
          <div>
            <label className="f">Mutfak kapanış</label>
            <input type="time" value={draft.kitchenClose.slice(0, 5)}
                   onChange={(e) => set({ kitchenClose: e.target.value + ':00' })} />
          </div>
        </div>
        <label className="chk" style={{ marginTop: 12 }}>
          <input type="checkbox" checked={draft.remindersEnabled}
                 onChange={(e) => set({ remindersEnabled: e.target.checked })} />
          <span>
            <span className="t">Hatırlatıcılar açık</span><br />
            <span className="d">Su hatırlatıcısı 10:00–20:00 arası çift saatlerde otomatik çalışır</span>
          </span>
        </label>
      </div>

      <div className="card">
        <h2>Antrenman planı</h2>
        <Prose className="card-h">
          Haftalık program ve telafi günü bunlardan hesaplanıyor. Bölünme seçimi
          (tüm vücut / üst-alt / itme-çekme-bacak) Programım sekmesinde.
        </Prose>
        <div className="grid g3">
          <div>
            <label className="f">Haftada kaç kuvvet antrenmanı</label>
            <input type="number" min={0} max={7} value={draft.strengthPerWeek}
                   onChange={(e) => set({ strengthPerWeek: +e.target.value })} />
          </div>
          <div>
            <label className="f">Telafi günü</label>
            <select value={draft.makeupWeekday}
                    onChange={(e) => set({ makeupWeekday: +e.target.value })}>
              {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="f">Telafi saati</label>
            <input type="time" value={draft.makeupTime.slice(0, 5)}
                   onChange={(e) => set({ makeupTime: e.target.value + ':00' })} />
          </div>
          <div>
            <label className="f">Seans süresi (dk)</label>
            <input type="number" min={15} max={240} value={draft.sessionMinutes}
                   onChange={(e) => set({ sessionMinutes: +e.target.value })} />
          </div>
          <div>
            <label className="f">Spor öncesi ara öğün (dk önce)</label>
            <input type="number" min={0} max={480} value={draft.preTennisMinutes}
                   onChange={(e) => set({ preTennisMinutes: +e.target.value })} />
          </div>
          <div>
            <label className="f">Spor sonrası öğün (dk sonra)</label>
            <input type="number" min={0} max={180} value={draft.postTennisMinutes}
                   onChange={(e) => set({ postTennisMinutes: +e.target.value })} />
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Masa başı sayacı</h2>
        <div className="grid g3">
          <div>
            <label className="f">Çalışma (dk)</label>
            <input type="number" min={10} max={120} value={draft.workMinutes}
                   onChange={(e) => set({ workMinutes: +e.target.value })} />
          </div>
          <div>
            <label className="f">Mola (dk)</label>
            <input type="number" min={1} max={30} value={draft.breakMinutes}
                   onChange={(e) => set({ breakMinutes: +e.target.value })} />
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Bildirimler</h2>
        <Prose className="card-h">
          Uygulama kapalıyken de öğün ve mutfak kapanış hatırlatıcıları gelsin istiyorsan
          bu cihazı kaydet.
        </Prose>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {!push.subscribed
            ? <button className="primary" onClick={() => push.subscribe()}
                      disabled={!push.supported || push.busy}>
                Bu cihaza bildirim gönder
              </button>
            : <button onClick={() => push.unsubscribe()} disabled={push.busy}>
                Bildirimleri kapat
              </button>}
          <button onClick={() => push.test()} disabled={!push.subscribed || push.busy}>
            Test bildirimi gönder
          </button>
        </div>
        <Prose className="note">{push.status}</Prose>
        <div className="warnbox">
          iOS'ta Web Push <strong>yalnızca ana ekrana eklenmiş</strong> uygulamada çalışır —
          Safari sekmesinde çalışmaz. Telefonda bildirim istiyorsan önce Paylaş → "Ana Ekrana Ekle".
        </div>
      </div>

      {saveProfile.isError && (
        <p className="note" style={{ color: 'var(--crit)' }}>
          Kaydedilemedi: {saveProfile.error instanceof Error ? saveProfile.error.message : 'bilinmeyen hata'}
        </p>
      )}

      {/* Kaydedilmemiş değişiklik varken ekranın altına yapışan şerit. */}
      {(dirty || saved) && (
        <div className="savebar" role="status">
          <span className="savebar-text">
            {saveProfile.isPending ? 'Kaydediliyor…'
              : saved && !dirty ? '✓ Kaydedildi'
              : 'Kaydedilmemiş değişiklik var'}
          </span>
          {dirty && (
            <>
              <button onClick={() => { setDraft(profile); setSaved(false) }}>Geri al</button>
              <button className="primary" onClick={save}
                      disabled={saveProfile.isPending || hataliBoy || hataliKilo}>
                Kaydet
              </button>
            </>
          )}
        </div>
      )}
    </>
  )
}
